import React, { useCallback, useEffect, useState, Fragment, Component } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronRightIcon, HomeIcon, LoaderIcon, AlertCircleIcon, BookOpenIcon, ClockIcon, TrendingUpIcon, GlobeIcon, ShieldCheckIcon, HeartIcon, UsersIcon, CheckCircleIcon, BriefcaseIcon, DollarSignIcon, ChevronDownIcon, ChevronUpIcon, CrossIcon } from 'lucide-react';
import { degreeInterestOptions, focusOptions, getSpecializationsForFocus } from '../data/degrees';
import { getGuideBySlug } from '../data/guides';
import { getStaticGuideListings, listingDisclosure } from '../data/guideListings';
import { UniversityListingCard } from '../components/UniversityListingCard';
import { searchSchools, MatchedSchool } from '../services/matchingApi';
import { MatchedSchoolCard, DidYouKnowBanner } from '../components/MatchedSchoolCard';
import { TrustSignals } from '../components/TrustSignals';
import { useFormState } from '../hooks/useFormState';
import { usePageMeta } from '../hooks/usePageMeta';
const benefitIconMap: Record<string, React.ElementType> = {
  ClockIcon,
  TrendingUpIcon,
  GlobeIcon,
  BookOpenIcon,
  ShieldCheckIcon,
  HeartIcon,
  UsersIcon,
  CheckCircleIcon
};
export function GuidePage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const guide = slug ? getGuideBySlug(slug) : undefined;
  usePageMeta(guide?.metaTitle || 'Guide | FindOnlineColleges', guide?.metaDescription || 'Explore accredited online colleges and degree programs.', {
    ogImage: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=1200&h=630&fit=crop&q=80',
    ogType: 'article'
  });
  const {
    formData,
    errors,
    updateFormData,
    validateHeroForm
  } = useFormState({
    areaOfStudy: guide?.focusValue || ''
  });
  const filteredSpecializations = formData.areaOfStudy ? getSpecializationsForFocus(formData.areaOfStudy) : [];
  // API state
  const [matchedSchools, setMatchedSchools] = useState<MatchedSchool[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const handleAreaOfStudyChange = (value: string) => {
    updateFormData('areaOfStudy', value);
    updateFormData('specialization', '');
  };
  // Some guides carry a fixed editorial line-up instead of live API results.
  const staticListings = getStaticGuideListings(slug);
  const fetchResults = useCallback(async (degreeInterest: string, areaStudy: string, mProgram: string) => {
    // Curated guides never query the feed; their line-up is fixed.
    if (staticListings) return;
    setIsLoading(true);
    setApiError(null);
    setHasSearched(true);
    const result = await searchSchools({
      degreeInterest: degreeInterest || undefined,
      areaStudy: areaStudy || undefined,
      mProgram: mProgram || undefined
    });
    if (result.success) {
      setMatchedSchools(result.schools);
    } else {
      setApiError('Unable to load school matches. Please try again.');
      setMatchedSchools([]);
    }
    setIsLoading(false);
  }, [staticListings]);
  // Auto-fetch on page load with the guide's focus value
  useEffect(() => {
    if (staticListings) return;
    if (guide?.focusValue) {
      fetchResults('', guide.focusValue, guide.defaultSpecialization || '');
    }
  }, [guide?.focusValue, guide?.defaultSpecialization, fetchResults, staticListings]);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  const handleSidebarSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateHeroForm()) {
      fetchResults(formData.degreeInterest, formData.areaOfStudy, formData.specialization);
    }
  };
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  if (!guide) {
    return <div className="min-h-[60vh] flex flex-col items-center justify-center bg-light px-4" data-id="element-1752" data-mp="qkgk8szfhw4">
        <h1 className="text-3xl font-bold text-navy mb-4" data-id="element-1753" data-mp="5pf1bgaifgiv">Guide Not Found</h1>
        <p className="text-gray-600 mb-8" data-id="element-1754" data-mp="aiq7rgbetva9">
          We couldn't find the guide you're looking for.
        </p>
        <Link to="/" className="text-white bg-navy px-6 py-2 rounded-md hover:bg-navy-light transition-colors" data-id="element-1755" data-mp="fjdx7xjzq42d">
          Return Home
        </Link>
      </div>;
  }
  return <main className="flex-grow bg-light pb-16" data-id="element-1756" data-mp="3j04a8wdzphdk">
      {/* Page Header */}
      {/* The header is deliberately compact on phones: the first advertiser
          listing has to be reachable without scrolling, so the breadcrumbs and
          intro paragraph only appear once there is room for them. */}
      <div className="hero-bg text-white py-6 sm:py-12" data-id="element-1757" data-mp="13987q8gu7gjg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1758" data-mp="1775xpdifm6nw">
          {/* Breadcrumbs */}
          <nav className="hidden sm:flex text-sm text-gray-300 mb-6" aria-label="Breadcrumb" data-id="element-1759" data-mp="2rpgjldvbaata">
            <ol className="inline-flex items-center space-x-1 md:space-x-3" data-id="element-1760" data-mp="2nr0yjfisjcii">
              <li className="inline-flex items-center" data-id="element-1761" data-mp="2iwwnol6emkc5">
                <Link to="/" className="inline-flex items-center hover:text-white transition-colors" data-id="element-1762" data-mp="2dzcy4p49al4w">
                  <HomeIcon className="w-4 h-4 mr-2" data-id="element-1763" data-mp="291bk4ilotc0a" />
                  Home
                </Link>
              </li>
              <li data-id="element-1764" data-mp="243a0nwkcnaoa">
                <div className="flex items-center" data-id="element-1765" data-mp="1zz279m2y56jq">
                  <ChevronRightIcon className="w-4 h-4 mx-1" data-id="element-1766" data-mp="1zzvgctpdo851" />
                  <span className="ml-1 md:ml-2" data-id="element-1767" data-mp="1v10rba0thnch">Guides</span>
                </div>
              </li>
              <li aria-current="page" data-id="element-1768" data-mp="3udmr5wjg0y22">
                <div className="flex items-center" data-id="element-1769" data-mp="3uclcli37xtfl">
                  <ChevronRightIcon className="w-4 h-4 mx-1" data-id="element-1770" data-mp="3pdqi3ivw2gfn" />
                  <span className="ml-1 md:ml-2 text-white font-medium" data-id="element-1771" data-mp="3pcfhtt1njqta">
                    {guide.breadcrumbLabel}
                  </span>
                </div>
              </li>
            </ol>
          </nav>

          {guide.heroEyebrow && <div className="hidden sm:inline-flex items-center px-3 py-1 rounded-full bg-white/10 border border-white/15 text-xs font-semibold mb-4 text-gold-light uppercase tracking-widest" data-id="element-1772" data-mp="3dvpdmg7hr91">
              <CrossIcon className="w-3.5 h-3.5 mr-2" data-id="element-1773" data-mp="w18bxwmd9z2c" />
              {guide.heroEyebrow}
            </div>}

          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-2 sm:mb-4 text-white" style={{
          textAlign: "left"
        }} data-id="element-1774" data-mp="101san4ml9aql">
            {guide.heroHeadline}
          </h1>
          <p className="hidden sm:block text-lg text-gray-300 max-w-3xl" data-id="element-1775" data-mp="r1c7jw9rc5m9">
            {guide.heroDescription}
          </p>
        </div>
      </div>

      <div className="hidden md:block" data-id="element-1776" data-mp="36vw19covugrx">
        <TrustSignals data-id="element-1777" data-mp="3bsw5di2699ge" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-12" data-id="element-1778" data-mp="3btxj5uakoa1t">
        <div className="flex flex-col lg:flex-row gap-8" data-id="element-1779" data-mp="347sao9ehfgmq">
          {/* Main Content - Listings */}
          <div className="lg:w-3/4 order-1" data-id="element-1780" data-mp="2z98me2cf1sr7">
            {/* Curated listings. Editorially written and always shown in the
                same order, so they bypass the API states entirely. */}
            {staticListings && <div data-id="element-1781" data-mp="388lx0yca0ndi">
                <div className="space-y-5" data-id="element-1782" data-mp="2kl44h0l6tl9h">
                  {staticListings.map(listing => <UniversityListingCard key={listing.id} listing={listing} data-id="element-1783" data-mp="2gjiwntnifx65" />)}
                </div>
                <p className="mt-6 text-xs text-gray-500 leading-relaxed" data-id="element-1784" data-mp="2ucs0zfwlpsht">
                  {listingDisclosure}
                </p>
              </div>}

            {/* Loading State */}
            {!staticListings && isLoading && <div className="flex flex-col items-center justify-center py-16" data-id="element-1785" data-mp="1wui7c4cq1le9">
                <LoaderIcon className="w-8 h-8 text-navy animate-spin mb-4" data-id="element-1786" data-mp="1ca7ytv3cb2mx" />
                <p className="text-gray-600 font-medium" data-id="element-1787" data-mp="1gbbh0o8v1arb">
                  Finding your best school matches...
                </p>
              </div>}

            {/* Error State */}
            {!staticListings && !isLoading && apiError && <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-start gap-3" data-id="element-1788" data-mp="1qb29kkdv1for">
                <AlertCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-1789" data-mp="1v82egryz4cea" />
                <div data-id="element-1790" data-mp="1z8uohnfo6lbm">
                  <p className="text-red-800 font-medium" data-id="element-1791" data-mp="1z82vmkdgj1p1">{apiError}</p>
                  <p className="text-red-600 text-sm mt-1" data-id="element-1792" data-mp="242710z72r1o0">
                    Try adjusting your search criteria or check back later.
                  </p>
                </div>
              </div>}

            {/* Results */}
            {!staticListings && !isLoading && !apiError && matchedSchools.length > 0 && <div className="space-y-4" data-id="element-1793" data-mp="2iw23nngmzuqr">
                {matchedSchools.map((school, idx) => <Fragment key={(school as Record<string, unknown>).schoolid as string || idx} data-id="element-1794" data-mp="78qd6y6wskum" data-mpi={idx}>
                    <MatchedSchoolCard school={school} index={idx} data-id="element-1795" data-mp="3tjuge35bzb4y" />
                    {idx === 0 && matchedSchools.length > 1 && <DidYouKnowBanner data-id="element-1796" data-mp="q1khmy6woukl" />}
                  </Fragment>)}
              </div>}

            {/* Empty State */}
            {!staticListings && !isLoading && !apiError && hasSearched && matchedSchools.length === 0 && <div className="bg-white rounded-xl border border-gray-200 p-12 text-center" data-id="element-1797" data-mp="bc4t7ouul182">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4" data-id="element-1798" data-mp="18t3h4v1q1qhv">
                    <AlertCircleIcon className="w-8 h-8 text-gray-400" data-id="element-1799" data-mp="13z90k0hexrn0" />
                  </div>
                  <h3 className="text-xl font-bold text-navy mb-2" data-id="element-1800" data-mp="2beeww2yoog23">
                    No matches found
                  </h3>
                  <p className="text-gray-500 max-w-md mx-auto" data-id="element-1801" data-mp="2la8i0aqgxhvh">
                    Try adjusting your degree level or program selection in the
                    sidebar to find more results.
                  </p>
                </div>}
          </div>

          {/* Sidebar */}
          <div className="lg:w-1/4 order-2" data-id="element-1802" data-mp="2q9us4bb02i02">
            <div className="sticky top-24 space-y-6" data-id="element-1803" data-mp="2q8tdjwurzddl">
              {/* Lead Form */}
              <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden" data-id="element-1804" data-mp="33zy9anv0vlin">
                <div className="bg-navy px-6 py-4" style={{
                backgroundColor: "#5FA396"
              }} data-id="element-1805" data-mp="2z2y5kjlnauup">
                  <h3 className="text-white font-bold text-center text-sm tracking-widest uppercase" data-id="element-1806" data-mp="3e08o2cwph0zx">
                    Get Matched Fast
                  </h3>
                  <p className="text-gray-300 text-xs text-center mt-1" data-id="element-1807" data-mp="3slw7twyzy3i9">
                    Find the right program for you
                  </p>
                </div>

                <form onSubmit={handleSidebarSubmit} className="p-5 space-y-4" data-id="element-1808" data-mp="hagfp5ndtmm">
                  <div data-id="element-1809" data-mp="3imwyec6eq3u7">
                    <select id="sidebar-degreeInterest" value={formData.degreeInterest} onChange={e => updateFormData('degreeInterest', e.target.value)} className={`block w-full px-3 py-2.5 text-sm border rounded-md bg-white appearance-none cursor-pointer ${errors.degreeInterest ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`} data-id="element-1810" data-mp="3ipswq1m62uj6">
                      <option value="" data-id="element-1811" data-mp="2pyretsr3akvy">Select Degree Level</option>
                      {degreeInterestOptions.map(opt => <option key={opt.value} value={opt.value} data-id="element-1812" data-mp="2uxm1j5s6gtlc">
                          {opt.type}
                        </option>)}
                    </select>
                  </div>

                  <div data-id="element-1813" data-mp="270pi9ahvxcg3">
                    <select id="sidebar-areaOfStudy" value={formData.areaOfStudy} onChange={e => handleAreaOfStudyChange(e.target.value)} className={`block w-full px-3 py-2.5 text-sm border rounded-md bg-white appearance-none cursor-pointer ${errors.areaOfStudy ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`} data-id="element-1814" data-mp="223pex7cf6nso">
                      <option value="" data-id="element-1815" data-mp="2fv48g83ye7dz">Select Area of Study</option>
                      {focusOptions.map(opt => <option key={opt.value} value={opt.value} data-id="element-1816" data-mp="19fjr2bq2kiom">
                          {opt.type}
                        </option>)}
                    </select>
                  </div>

                  <div data-id="element-1817" data-mp="3g429vp76yvsi">
                    <select id="sidebar-specialization" value={formData.specialization} onChange={e => updateFormData('specialization', e.target.value)} disabled={!formData.areaOfStudy} className={`block w-full px-3 py-2.5 text-sm border rounded-md bg-white appearance-none cursor-pointer disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed ${errors.specialization ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`} data-id="element-1818" data-mp="3g4vi6ulsttcr">
                      <option value="" data-id="element-1819" data-mp="hklweyuapaiv">
                        {formData.areaOfStudy ? 'Select Program' : 'Select an area of study first'}
                      </option>
                      {filteredSpecializations.map(opt => <option key={opt.value} value={opt.value} data-id="element-1820" data-mp="dny8vzo42d54">
                          {opt.type}
                        </option>)}
                    </select>
                  </div>

                  <button type="submit" disabled={isLoading} className="w-full bg-cta hover:bg-cta-dark disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold py-4 px-4 rounded-lg text-sm tracking-wider uppercase transition-colors duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2" style={{
                  backgroundColor: "#E3924E"
                }} data-id="element-1821" data-mp="moe8gy1qh04p">
                    {isLoading && <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-1822" data-mp="3r39zfnxeaqb" />}
                    {isLoading ? 'Searching...' : 'Search Programs'}
                  </button>
                </form>
              </div>

              {/* Undecided nudge — for visitors who can't fill the form yet */}
              <Link to="/degrees" className="group flex items-center gap-3 bg-light hover:bg-white border border-gray-200 rounded-lg px-4 py-3 transition-colors duration-200" data-id="element-1823" data-mp="33ebb8odu3xna">
                <span className="flex-shrink-0 w-9 h-9 rounded-full bg-white group-hover:bg-light border border-gray-200 flex items-center justify-center text-navy transition-colors duration-200" data-id="element-1824" data-mp="2y1wiyd2z3tot">
                  <UsersIcon className="w-4 h-4" aria-hidden="true" data-id="element-1825" data-mp="2oa8ijmofv2b7" />
                </span>
                <span className="min-w-0 flex-1" data-id="element-1826" data-mp="3c0cnpyh9za4b">
                  <span className="block text-sm font-bold text-navy leading-tight" data-id="element-1827" data-mp="3byjecbfa2bsm">
                    Not sure what to study?
                  </span>
                  <span className="block text-xs text-gray-600 mt-0.5 leading-tight" data-id="element-1828" data-mp="32382c0v97iok">
                    Explore top programs by career
                  </span>
                </span>
                <ChevronRightIcon className="w-4 h-4 flex-shrink-0 text-gray-400 group-hover:text-navy transition-colors duration-200" aria-hidden="true" data-id="element-1829" data-mp="1qmrqehiwrk6g" />
              </Link>

              {/* What to Look For */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-id="element-1830" data-mp="25giyqw61zad">
                <h4 className="font-bold text-navy mb-4 border-b border-gray-100 pb-2" data-id="element-1831" data-mp="3tc1eonx61vdq">
                  {guide.sidebarChecklistTitle}
                </h4>
                <ul className="space-y-2.5" data-id="element-1832" data-mp="3jj3tub4pbuby">
                  {guide.sidebarChecklist.map((item, i) => <li key={i} className="flex items-start gap-2 text-sm text-gray-700" data-id="element-1833" data-mp="3eltvu738yaci" data-mpi={i}>
                      <CheckCircleIcon className="w-4 h-4 text-crimson flex-shrink-0 mt-0.5" data-id="element-1834" data-mp="35kcekle3s2m8" />
                      <span data-id="element-1835" data-mp="30l9dfc18ugjj">{item}</span>
                    </li>)}
                </ul>
              </div>

              {/* Related Links */}
              {guide.relatedLinks.length > 0 && <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-id="element-1836" data-mp="yu62elnfyzyg">
                  <h4 className="font-bold text-navy mb-4 border-b border-gray-100 pb-2" data-id="element-1837" data-mp="16dp07y1cgxr3">
                    Related
                  </h4>
                  <ul className="space-y-2" data-id="element-1838" data-mp="1bb7b3tyx9n1q">
                    {guide.relatedLinks.map(link => <li key={link.href} data-id="element-1839" data-mp="1g4jqrelmijic">
                        <Link to={link.href} className="text-sm flex items-center text-gray-600 hover:text-crimson" data-id="element-1840" data-mp="1l2bposkor5w1">
                          <ChevronRightIcon className="w-3 h-3 mr-1 flex-shrink-0" data-id="element-1841" data-mp="sdo0ilwtfwd7" />
                          {link.label}
                        </Link>
                      </li>)}
                  </ul>
                </div>}
            </div>
          </div>
        </div>
      </div>

      {/* Guide Content Sections */}
      <div className="bg-white border-t border-gray-200 mt-12 pt-16 pb-20" data-id="element-1842" data-mp="28r4fs8emmjiq">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1843" data-mp="2dq7mdxa98xst">
          {/* Overview */}
          <div className="max-w-4xl mx-auto text-center mb-16" data-id="element-1844" data-mp="2yui4a3hyj8dg">
            <h2 className="text-3xl font-bold text-navy mb-6" data-id="element-1845" data-mp="2ysouwgfyma1r">
              {guide.overviewHeading}
            </h2>
            <div className="prose prose-lg text-gray-600 mx-auto leading-relaxed space-y-6 text-left" data-id="element-1846" data-mp="38kkuxx1lwa8m">
              {guide.overviewParagraphs.map((p, i) => <p key={i} data-id="element-1847" data-mp="33stewgg2tmwq" data-mpi={i}>{p}</p>)}
            </div>
          </div>

          {/* Benefits */}
          <div className="mb-20 bg-gray-50 rounded-2xl p-8 sm:p-12 border border-gray-200" data-id="element-1848" data-mp="3hnlzvueqjvap">
            <div className="text-center mb-12" data-id="element-1849" data-mp="3dm0fzp3sagr0">
              <h2 className="text-2xl sm:text-3xl font-bold text-navy mb-4" data-id="element-1850" data-mp="3rjyssq2gi54e">
                {guide.benefitsHeading}
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-1851" data-mp="3ml5dcs3m041l">
                {guide.benefitsIntro}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1852" data-mp="1s6kyiw1yz2q4">
              {guide.benefits.map((benefit, i) => {
              const IconComponent = benefitIconMap[benefit.icon] || BookOpenIcon;
              return <div key={i} className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm text-center" data-id="element-1853" data-mp="cmvgd53jw0dx" data-mpi={i}>
                    <div className="w-12 h-12 mx-auto bg-navy/10 rounded-lg flex items-center justify-center mb-4" data-id="element-1854" data-mp="2wseefegbc3g">
                      <IconComponent className="w-6 h-6 text-navy" data-id="element-1855" data-mp="3l1vneb7jg895" />
                    </div>
                    <h3 className="text-lg font-bold text-navy mb-2" data-id="element-1856" data-mp="3q3bb6b13osv8">
                      {benefit.title}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1857" data-mp="3b7n7cmzp27rv">
                      {benefit.description}
                    </p>
                  </div>;
            })}
            </div>
          </div>

          {/* Careers */}
          <div className="mb-20" data-id="element-1858" data-mp="1ebxqga0h2mzq">
            <div className="text-center mb-10" data-id="element-1859" data-mp="1eawg67ra64j2">
              <h2 className="text-2xl font-bold text-navy mb-4" data-id="element-1860" data-mp="2hwwynaeqjqbq">
                {guide.careersHeading}
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-1861" data-mp="2mooqrpdji43z">
                {guide.careersIntro}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1862" data-mp="28uns0vml8h3m">
              {guide.careers.map((career, i) => <div key={i} className="bg-gray-50 rounded-xl p-6 border border-gray-100 hover:shadow-md transition-shadow duration-200 flex flex-col h-full" style={{
              alignItems: "center",
              justifyContent: "flex-start"
            }} data-id="element-1863" data-mp="31kwbqrwrv9kb" data-mpi={i}>
                  <div className="w-12 h-12 bg-navy/10 rounded-lg flex items-center justify-center mb-4 text-navy" data-id="element-1864" data-mp="2rm6tgy0yrn3j">
                    <BriefcaseIcon className="w-6 h-6" data-id="element-1865" data-mp="2wk8cjiye3mf0" />
                  </div>
                  <h3 className="text-lg font-bold text-navy mb-2 leading-tight" style={{
                textAlign: "center"
              }} data-id="element-1866" data-mp="1g71plqp3jiic">
                    {career.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-6 flex-grow" data-id="element-1867" data-mp="1dmls5lg8b2rc">
                    {career.desc}
                  </p>
                  <div className="pt-4 border-t border-gray-200 mt-auto" data-id="element-1868" data-mp="18nsdvqt4b7q4">
                    <div className="flex items-center text-sm font-semibold text-gray-800" data-id="element-1869" data-mp="1mobh8kmn1ply">
                      <DollarSignIcon className="w-4 h-4 text-navy-light mr-1" data-id="element-1870" data-mp="1hoqk8fr13rbv" />
                      {career.salary}
                    </div>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-1871" data-mp="tznt8hai8m4p">
                      Estimated Annual Salary
                    </p>
                  </div>
                </div>)}
            </div>
          </div>

          {/* FAQs */}
          <div className="max-w-3xl mx-auto" data-id="element-1872" data-mp="yxz32s0suwmx">
            <div className="text-center mb-10" data-id="element-1873" data-mp="yx66gjvk1nii">
              <h2 className="text-2xl font-bold text-navy mb-4" data-id="element-1874" data-mp="b8ehe1d9hl9y">
                {guide.faqHeading}
              </h2>
              <p className="text-gray-600" data-id="element-1875" data-mp="68s8230k0p6f">{guide.faqIntro}</p>
            </div>

            <div className="space-y-4" data-id="element-1876" data-mp="26ckz3jgyz6s7">
              {guide.faqs.map((faq, index) => <div key={index} className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm transition-all duration-200" data-id="element-1877" data-mp="2baw51j458x55" data-mpi={index}>
                  <button className="w-full px-6 py-4 text-left flex justify-between items-center focus:outline-none focus:ring-2 focus:ring-inset focus:ring-navy" onClick={() => toggleFaq(index)} aria-expanded={openFaqIndex === index} data-id="element-1878" data-mp="2l0pikpnujmc1">
                    <span className="font-semibold text-navy pr-8" data-id="element-1879" data-mp="1xb4wvz0b6az2">
                      {faq.q}
                    </span>
                    {openFaqIndex === index ? <ChevronUpIcon className="w-5 h-5 text-gray-500 flex-shrink-0" data-id="element-1880" data-mp="38qtlsvx4hk2i" /> : <ChevronDownIcon className="w-5 h-5 text-gray-500 flex-shrink-0" data-id="element-1881" data-mp="3crlu9my67kxq" />}
                  </button>
                  {openFaqIndex === index && <div className="px-6 pb-5 pt-0" data-id="element-1882" data-mp="x3gbls3ywa2w">
                      <p className="text-gray-600 text-sm leading-relaxed border-t border-gray-100 pt-4 mt-2" data-id="element-1883" data-mp="x3q2rj0r0na6">
                        {faq.a}
                      </p>
                    </div>}
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </main>;
}